import {Injectable} from '@angular/core';
import {SettingsService, ActivityFilter, StatusLayerType} from '../../../../configuration/settings.service';
import {ShiftsManagerService} from '../../../../data/shifts/shifts-manager.service';
import {Subscription} from 'rxjs';
import {ShiftModel, ShiftState} from '../../../models/shift.model';
import {LayerSpecification, LineLayerSpecification, LngLatBounds, SymbolLayerSpecification} from 'maplibre-gl';
import {MapLayersManager} from '../map-layers-manager';
import {MapStyles, VehicleColor} from '../../../../configuration/map-styles';
import {LiveMapDataService} from '../../../../pages/live-map/services/live-map-data.service';
import {VehicleModelWithActiveShift} from '../../../models/vehicle.model';
import {MapFilters} from '../../../../configuration/map-filters';
import {FeatureCollection, GeoJSON, LineString} from 'geojson';
import {ConfigurationService} from '../../../../configuration/configuration.service';
import {Asset} from '../../../../pages/live-map/models/asset.class';
import {AssetsManagerService} from '../../../../data/assets/assets-manager.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {MainRoute} from '../../../models/angular-routing';
import { TrackStyles } from '../../../../configuration/model/TrackStyles';
import { LocationTrackingPollerService } from '../../../../data/server-events/location-tracking-poller.service';
import { ConfigurationModel } from '../../../models/configuration.model';
import { TracksVectorTilesLayerService } from './tracks-vector-tiles-layer.service';

@Injectable({
  providedIn: 'root'
})
export class TracksGeoJsonLayerService {

  // normal mode layers
  static readonly TRACKS_GEOJSON_SOURCE_ID = 'tracks-geojson';
  static readonly LAYER_ID_TRACK_GEOJSON = 'track-geojson';
  static readonly LAYER_ID_TRACK_GEOJSON_PADDING = 'track-geojson-padding'; // clickable transparent
  static readonly LAYER_ID_TRACK_HISTORY_GEOJSON = 'track-geojson-history';
  static readonly LAYER_ID_TRACK_HISTORY_GEOJSON_PADDING = 'track-geojson-history-padding';

  // selected active shift mode layers
  static readonly LAYER_ID_TRACK_ACTIVE_SHIFT = 'track-geojson-active-shift';
  static readonly LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING = 'track-geojson-active-shift-padding';
  static readonly LAYER_ID_PLOW_DOWN_ACTIVE_SHIFT = 'track-geojson-active-shift-plow-down';
  static readonly LAYER_ID_SPREADER_ON_ACTIVE_SHIFT = 'track-geojson-active-shift-spreader-on';
  static readonly LAYER_ID_ARROWS_ACTIVE_SHIFT = 'track-geojson-active-shift-arrows';

  private mapLayersManager: MapLayersManager;
  private lineLayers: LayerSpecification[] = [];
  private sourceData: FeatureCollection;

  private readonly openSubscriptions = Array<Subscription>();

  // filtered vehicle ID list
  selectedVehicleIds = [];
  // all active shift IDs
  activeShiftIds = [];
  // vehicleId if Vehicle Detail panel is on
  vehicleDetailMode = 0;
  highlightedShift: ShiftModel = null;
  // selected track layer type
  private currentTheme: StatusLayerType = null;
  // live feed?
  isEnabled: boolean;
  // custom vehicle colors
  customColors: VehicleColor[] = [];
  // flags filter
  private flagsFilter: any[] = [];
  // recent toggle on track bar
  showRecent = false;
  // vector tile cache should not be older than 1 hour
  hours: number;
  defaultHoursAgoFilter: number;
  private trackStyles: TrackStyles;
  private contentVisibilityCutoff: Date = null;

  constructor(private router: Router,
              private activatedRoute: ActivatedRoute,
              private locationTrackingPoller: LocationTrackingPollerService,
              private settingsService: SettingsService,
              private shiftsManagerService: ShiftsManagerService,
              private assetManager: AssetsManagerService,
              private liveMapDataService: LiveMapDataService,
              private configurationService: ConfigurationService,
              ) { }

  init(mapLayersManager: MapLayersManager, configuration: ConfigurationModel, isEnabled: boolean) {
    if (!!this.mapLayersManager) {
      throw Error('The map layers manager has already been set.');
    }
    this.mapLayersManager = mapLayersManager;
    this.defaultHoursAgoFilter = configuration.lookBackPeriod;
    this.hours = configuration.lookBackPeriod;
    this.isEnabled = isEnabled;
    this.trackStyles = this.configurationService.trackStyles;
    this.addSourceAndLayers();
    this.connectToManager();
  }

  release() {
    if (!this.mapLayersManager) {
      throw Error('The map has not been set!');
    }

    for (const subscription of this.openSubscriptions) {
      subscription.unsubscribe();
    }
    this.openSubscriptions.length = 0;
    this.mapLayersManager = null;
  }

  private handleGeoJsonUpdate(geojson: GeoJSON) {
    this.sourceData = geojson as FeatureCollection;
    this.mapLayersManager
      ?.getGeoJsonSource(TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID)
      ?.setData(geojson);
  }

  addSourceAndLayers() {
    this.mapLayersManager.addSource(TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: []
      }
    });

    // add layers
    this.currentTheme = this.settingsService.getStringValue(SettingsService.TRACKS_LAYER_TYPE_KEY) as StatusLayerType;
    const isVisible = this.isEnabled ? 'visible' : 'none';
    this.getLineLayers().forEach(layer => {
      layer.layout.visibility = isVisible;
      this.lineLayers.push(layer);
      this.mapLayersManager.addLayer(layer as LineLayerSpecification | SymbolLayerSpecification, this.mapLayersManager.firstSymbolLayerId);
    });
  }

  private handleCustomVehicleMapColor(vehicles: VehicleModelWithActiveShift[]) {
    this.customColors = vehicles
        .filter(vehicle => !!vehicle.mapColor)
        .map(vehicle => {
          return {id: vehicle.id, color: vehicle.mapColor};
        });
    this.updateFiltersAndStyles();
  }

  private handleHighlightedShiftChanged(shift: ShiftModel) {
    this.highlightedShift = shift;
    this.updateFiltersAndStyles();
  }

  private handleVehiclesChanged(assets: Asset[]) {
    const activeShiftIds = [];
    this.selectedVehicleIds = assets.map(asset => {
      if (asset.shiftStatus !== ShiftState.ENDED && !!asset.shiftId) {
        activeShiftIds.push(asset.shiftId);
      }
      return asset.id;
    });
    this.activeShiftIds = activeShiftIds;
    this.updateFiltersAndStyles();
  }

  private handleTrackFilterChanged(trackFilter: ActivityFilter) {
    this.flagsFilter = MapFilters.getTrackFilter(trackFilter);
    this.updateFiltersAndStyles();
  }

  private handleShowRecentChanged(showRecent: boolean) {
    this.showRecent = showRecent;
    this.updateFiltersAndStyles();
  }

  private handleTimeFilterChanged(timeFilter: number) {
    this.hours = timeFilter;
    this.updateFiltersAndStyles();
  }

  private connectToManager() {
    const that = this;

    this.vehicleDetailMode =
      !!this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.routeConfig?.path
        ?.startsWith(MainRoute.VEHICLE + '/:id')
        ? this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.params?.id ?? 0
        : 0;

    const settingsChangedSubscription = this.settingsService.settingsChangedObservable.subscribe({
      next(newSettings) {
        if (newSettings.key === SettingsService.TRACKS_LAYER_TYPE_KEY) {
          that.currentTheme = newSettings.value;
          that.updateFiltersAndStyles();
        }
        if (newSettings.key === SettingsService.ACTIVITY_FILTER) {
          that.handleTrackFilterChanged(newSettings.value);
        }
        if (newSettings.key === SettingsService.PLOW_TRACK_SHOW_RECENT) {
          that.handleShowRecentChanged(newSettings.value);
        }
        if (newSettings.key === SettingsService.PLOW_TRACK_TIME_FILTER) {
          that.handleTimeFilterChanged(Number(newSettings.value));
        }
      }
    });
    this.openSubscriptions.push(settingsChangedSubscription);

    const routerSubscription = this.router.events.subscribe((event: any) => {
      if (event instanceof NavigationEnd) {
        const oldIsVehicleMode = this.vehicleDetailMode;
        this.vehicleDetailMode =
          !!this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.routeConfig?.path
            ?.startsWith(MainRoute.VEHICLE + '/:id')
            ? this.activatedRoute.firstChild?.firstChild?.firstChild?.firstChild?.snapshot?.params?.id ?? 0
            : 0;

        // if any change
        if (oldIsVehicleMode !== this.vehicleDetailMode) {
          this.updateFiltersAndStyles();
        }
      }
    });
    this.openSubscriptions.push(routerSubscription);

    const allVehiclesSubscription = this.liveMapDataService.vehicles$.subscribe(vehicles => {
      this.handleCustomVehicleMapColor(vehicles);
    });
    this.openSubscriptions.push(allVehiclesSubscription);

    const assetsSubscription = this.assetManager.visibleTrackAssets$.subscribe(assets => {
      this.handleVehiclesChanged(assets);
    });
    this.openSubscriptions.push(assetsSubscription);

    const highlightingUpdateSubscription = this.shiftsManagerService.highlightedShift$.subscribe({
      next(shiftFilterUpdate) {
        that.handleHighlightedShiftChanged(shiftFilterUpdate.shift);
      }
    });
    this.openSubscriptions.push(highlightingUpdateSubscription);

    const cacheChangeSubscription = this.locationTrackingPoller.historyLocationsObservable.subscribe({
      next(geojson: GeoJSON) {
        that.handleGeoJsonUpdate(geojson);
      }
    });
    this.openSubscriptions.push(cacheChangeSubscription);

    const trackStylesSubscription = this.configurationService.userTrackStyles$.subscribe(styles => {
      this.trackStyles = styles;
      this.updateFiltersAndStyles();
    });
    this.openSubscriptions.push(trackStylesSubscription);

    this.openSubscriptions.push(
      this.configurationService.sharedConfigurationModel.subscribe(model => {
        if (model) {
          this.contentVisibilityCutoff = model.contentVisibilityCutoff;
          this.mapLayersManager.waitUntilStyleLoaded(
            () => this.updateFiltersAndStyles()
          );
        }
      })
    );
  }

  private getTrackHistoryLayer(): LineLayerSpecification[] {
    const filter = !this.vehicleDetailMode && MapFilters.getInactiveShiftsFilter(
      this.currentTheme === StatusLayerType.GPS_TRACKS,
      this.activeShiftIds,
      this.selectedVehicleIds,
      this.hours === this.defaultHoursAgoFilter ? null : this.hours,
      this.contentVisibilityCutoff,
      this.flagsFilter,
      this.showRecent,
      false,
    );
    return [{
      id: TracksGeoJsonLayerService.LAYER_ID_TRACK_HISTORY_GEOJSON,
      source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
      type: 'line',
      filter,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': this.trackStyles.liveMapPlowInactive.color,
        'line-opacity': this.trackStyles.liveMapPlowInactive.opacity,
        'line-width': this.trackStyles.liveMapPlowInactive.width,
        'line-dasharray': [
          'literal',
          !!this.trackStyles.liveMapPlowInactive.dasharray ? this.trackStyles.liveMapPlowInactive.dasharray : [10]
        ],
      }
    },  {
      id: TracksGeoJsonLayerService.LAYER_ID_TRACK_HISTORY_GEOJSON_PADDING,
      source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
      type: 'line',
      filter,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ff0000',
        'line-opacity': 0,
        'line-width': 10,
      }
    }] as LineLayerSpecification[];
  }

  private getActiveTrackLayer(): LineLayerSpecification[] {
    const filter = !this.vehicleDetailMode && MapFilters.getActiveShiftsFilter(
      this.currentTheme === StatusLayerType.GPS_TRACKS,
      this.activeShiftIds,
      this.selectedVehicleIds,
      null,
      this.contentVisibilityCutoff,
      this.flagsFilter,
      this.highlightedShift,
      false,
    );
    return [{
      id: TracksGeoJsonLayerService.LAYER_ID_TRACK_GEOJSON,
      source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
      type: 'line',
      filter,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': this.getHighlightedColor(),
        'line-opacity': this.trackStyles.liveMapPlowLive.opacity,
        'line-width': this.getHighlightedWidth(),
        'line-dasharray': [
          'case',
          ['==', ['get', 'estimated'], true],
          ['literal', [5, 4]],
          ['literal', !!this.trackStyles.liveMapPlowLive.dasharray ? this.trackStyles.liveMapPlowLive.dasharray : [10]]
        ],
      }
    }, {
      id: TracksGeoJsonLayerService.LAYER_ID_TRACK_GEOJSON_PADDING,
      source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
      type: 'line',
      filter,
      layout: {
        'line-join': 'round',
        'line-cap': 'round'
      },
      paint: {
        'line-color': '#ff0000',
        'line-opacity': 0,
        'line-width': 10,
      }
    }] as LineLayerSpecification[];
  }

  private getSelectedShiftLayers(): LayerSpecification[] {
    return [
      {
        id: TracksGeoJsonLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT,
        type: 'line',
        source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
        filter: !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
            ActivityFilter.NONE, null, null, this.activeShiftIds, this.vehicleDetailMode,
        ),
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': this.trackStyles.shiftPlowNormal.color,
          'line-opacity': this.trackStyles.shiftPlowNormal.opacity,
          'line-width': this.trackStyles.shiftPlowNormal.width,
        }
      } as LineLayerSpecification,
      {
        id: TracksGeoJsonLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING,
        type: 'line',
        source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
        filter: !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
          ActivityFilter.NONE, null, null, this.activeShiftIds, this.vehicleDetailMode,
        ),
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': '#ff0000',
          'line-opacity': 0,
          'line-width': 10,
        }
      } as LineLayerSpecification,
      {
        id: TracksGeoJsonLayerService.LAYER_ID_PLOW_DOWN_ACTIVE_SHIFT,
        type: 'line',
        source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
        filter: !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
            ActivityFilter.PLOWING_MOWING_SWEEPING, null, null, this.activeShiftIds, this.vehicleDetailMode,
        ),
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': this.trackStyles.shiftPlowPlowDown.color,
          'line-opacity': this.trackStyles.shiftPlowPlowDown.opacity,
          'line-width': this.trackStyles.shiftPlowPlowDown.width,
          'line-offset': this.trackStyles.shiftPlowPlowDown.offset,
        }
      } as LineLayerSpecification,
      {
        id: TracksGeoJsonLayerService.LAYER_ID_SPREADER_ON_ACTIVE_SHIFT,
        type: 'line',
        source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
        filter: !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
            ActivityFilter.SPREADING, null, null, this.activeShiftIds, this.vehicleDetailMode,
        ),
        layout: {
          'line-join': 'round',
          'line-cap': 'round'
        },
        paint: {
          'line-color': this.trackStyles.shiftPlowSpreaderOn.color,
          'line-opacity': this.trackStyles.shiftPlowSpreaderOn.opacity,
          'line-width': this.trackStyles.shiftPlowSpreaderOn.width,
          'line-offset': this.trackStyles.shiftPlowSpreaderOn.offset,
        }
      } as LineLayerSpecification,
      {
        id: TracksGeoJsonLayerService.LAYER_ID_ARROWS_ACTIVE_SHIFT,
        type: 'symbol',
        source: TracksGeoJsonLayerService.TRACKS_GEOJSON_SOURCE_ID,
        filter: !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
            ActivityFilter.NONE, null, null, this.activeShiftIds, this.vehicleDetailMode,
        ),
        layout: {
          'symbol-placement': 'line',
          'icon-image': TracksVectorTilesLayerService.ICON_ARROW,
          'icon-rotate': 90,
          'icon-rotation-alignment': 'map',
          'icon-allow-overlap': true,
          'icon-ignore-placement': true
        },
        paint: {
        }
      } as SymbolLayerSpecification,
    ];
  }

  getLineLayers(): LayerSpecification[] {
    return [
      ...this.getTrackHistoryLayer(),
      ...this.getActiveTrackLayer(),
      ...this.getSelectedShiftLayers(),
    ];
  }

  updateFiltersAndStyles() {
    this.lineLayers.forEach(lineLayer => {
      switch (lineLayer.id) {
        case TracksGeoJsonLayerService.LAYER_ID_TRACK_HISTORY_GEOJSON:
          this.mapLayersManager.setFilter(
              lineLayer.id,
              !this.vehicleDetailMode && MapFilters.getInactiveShiftsFilter(
              this.currentTheme === StatusLayerType.GPS_TRACKS,
              this.activeShiftIds,
              this.selectedVehicleIds,
              this.hours === this.defaultHoursAgoFilter ? null : this.hours,
              this.contentVisibilityCutoff,
              this.flagsFilter,
              this.showRecent,
              false,
          ));
          this.mapLayersManager.setLayerPaintProperty(
              lineLayer.id,
              'line-color',
              this.trackStyles.liveMapPlowInactive.color,
          );
          this.mapLayersManager.setLayerPaintProperty(
              lineLayer.id,
              'line-width',
              this.trackStyles.liveMapPlowInactive.width,
          );
          this.mapLayersManager.setLayerPaintProperty(
            lineLayer.id,
            'line-dasharray',
            !!this.trackStyles.liveMapPlowInactive.dasharray ? ['literal',  this.trackStyles.liveMapPlowInactive.dasharray] : undefined,
          );
          break;
        case TracksGeoJsonLayerService.LAYER_ID_TRACK_HISTORY_GEOJSON_PADDING:
          this.mapLayersManager.setFilter(
            lineLayer.id,
            !this.vehicleDetailMode && MapFilters.getInactiveShiftsFilter(
              this.currentTheme === StatusLayerType.GPS_TRACKS,
              this.activeShiftIds,
              this.selectedVehicleIds,
              this.hours === this.defaultHoursAgoFilter ? null : this.hours,
              this.contentVisibilityCutoff,
              this.flagsFilter,
              this.showRecent,
              false,
            ));
          break;
        case TracksGeoJsonLayerService.LAYER_ID_TRACK_GEOJSON:
          this.mapLayersManager.setFilter(
              lineLayer.id,
              !this.vehicleDetailMode && MapFilters.getActiveShiftsFilter(
              this.currentTheme === StatusLayerType.GPS_TRACKS,
              this.activeShiftIds,
              this.selectedVehicleIds,
              null,
              this.contentVisibilityCutoff,
              this.flagsFilter,
              this.highlightedShift,
              false,
          ));
          this.mapLayersManager.setLayerPaintProperty(
              lineLayer.id,
              'line-color',
              this.getHighlightedColor(),
          );
          this.mapLayersManager.setLayerPaintProperty(
              lineLayer.id,
              'line-width',
              this.getHighlightedWidth(),
          );
          this.mapLayersManager.setLayerPaintProperty(
            lineLayer.id,
            'line-dasharray',
            [
              'case',
              ['==', ['get', 'estimated'], true],
              ['literal', [5, 4]],
              ['literal', !!this.trackStyles.liveMapPlowLive.dasharray ? this.trackStyles.liveMapPlowLive.dasharray : [10]]
            ],
          );
          break;
        case TracksGeoJsonLayerService.LAYER_ID_TRACK_GEOJSON_PADDING:
          this.mapLayersManager.setFilter(
            lineLayer.id,
            !this.vehicleDetailMode && MapFilters.getActiveShiftsFilter(
              this.currentTheme === StatusLayerType.GPS_TRACKS,
              this.activeShiftIds,
              this.selectedVehicleIds,
              null,
              this.contentVisibilityCutoff,
              this.flagsFilter,
              this.highlightedShift,
              false,
            ));
          break;
        case TracksGeoJsonLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT:
          this.mapLayersManager.setFilter(
              lineLayer.id,
              !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
                  ActivityFilter.NONE, null, null, this.activeShiftIds, this.vehicleDetailMode,
              ),
          );
          break;
        case TracksGeoJsonLayerService.LAYER_ID_TRACK_ACTIVE_SHIFT_PADDING:
          this.mapLayersManager.setFilter(
            lineLayer.id,
            !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
              ActivityFilter.NONE, null, null, this.activeShiftIds, this.vehicleDetailMode,
            ),
          );
          break;
        case TracksGeoJsonLayerService.LAYER_ID_PLOW_DOWN_ACTIVE_SHIFT:
          this.mapLayersManager.setFilter(
              lineLayer.id,
              !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
                  ActivityFilter.PLOWING_MOWING_SWEEPING, null, null, this.activeShiftIds, this.vehicleDetailMode,
              ),
          );
          break;
        case TracksGeoJsonLayerService.LAYER_ID_SPREADER_ON_ACTIVE_SHIFT:
          this.mapLayersManager.setFilter(
              lineLayer.id,
              !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
                  ActivityFilter.SPREADING, null, null, this.activeShiftIds, this.vehicleDetailMode,
              ),
          );
          break;
        case TracksGeoJsonLayerService.LAYER_ID_ARROWS_ACTIVE_SHIFT:
          this.mapLayersManager.setFilter(
              lineLayer.id,
              !!this.vehicleDetailMode && MapFilters.getShiftGeoJsonFilter(
                  ActivityFilter.NONE, null, null, this.activeShiftIds, this.vehicleDetailMode,
              ),
          );
          break;
        default:
          console.warn(`This should not happen! The map layer doesn't exist. ID: ${lineLayer.id}`);
      }
    });
  }

  private getHighlightedColor() {
    return !this.vehicleDetailMode && this.currentTheme === StatusLayerType.GPS_TRACKS
        ? MapStyles.getHighlightedColor(
            this.highlightedShift?.id !== undefined ? [this.highlightedShift.id] : null,
            this.trackStyles.liveMapPlowHighlighted.color,
            this.trackStyles.liveMapPlowLive.color,
            this.customColors,
        )
        : MapStyles.getHighlightedColor(
            this.highlightedShift?.id !== undefined ? [this.highlightedShift.id] : null,
            '#A0A0A0',
            this.trackStyles.liveMapPlowLive.color,
            this.customColors,
        );
  }

  private getHighlightedWidth() {
    return !this.vehicleDetailMode && this.currentTheme === StatusLayerType.GPS_TRACKS
        ? MapStyles.getHighlightedProperty(
            this.highlightedShift?.id !== undefined ? [this.highlightedShift.id] : null,
            this.trackStyles.liveMapPlowHighlighted.width,
            this.trackStyles.liveMapPlowLive.width,
        )
        : MapStyles.getHighlightedProperty(
            this.highlightedShift?.id !== undefined ? [this.highlightedShift.id] : null,
            this.trackStyles.liveMapPlowLive.width,
            this.trackStyles.liveMapPlowLive.width,
        );
  }

  public getBounds(shiftId: number) {
    const features = this.sourceData?.features?.filter(feature => feature.properties['sessionkey'] === shiftId);
    if (!!features && features.length > 0) {
      const coordinatesByFeature = features.map(feature => (feature.geometry as LineString).coordinates);
      // flatten the array Position[][] to Position[]
      const coordinates = coordinatesByFeature.reduce((accumulator, value) => accumulator.concat(value), []);
      if (!!coordinates && coordinates.length > 0) {
        return coordinates.reduce((bounds, position) => {
          return bounds.extend([position[0], position[1]]);
        }, new LngLatBounds([coordinates[0][0], coordinates[0][1]], [coordinates[0][0], coordinates[0][1]]));
      } else {
        return null;
      }
    } else {
      return null;
    }
  }
}
